import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownLeft,
  ArrowUpRight,
  Eye,
  Filter,
  Globe2,
  Lock,
  MapPin,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Plus,
  Power,
  PowerOff,
  Radio,
  RefreshCw,
  Search,
  Trash2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';
/* La cartographie du produit est une primitive partagée: une seule surface,
   une seule déclaration de fond de plan, une seule couche de zones. Rien de
   Leaflet n'est instancié ici — l'écran compose, il ne cartographie pas. */
import {
  MapLegend,
  MapSurface,
  ZoneLayer,
  ZonePicker,
  ZonesFiltre,
  filtrerZones,
  zoneFamily,
} from '../components/map';
import { useAuth } from '../context/AuthContext';
import { clientsAPI, geoLocationsAPI, stationsAPI, trucksAPI, usersAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { formatRelativeTime } from '../lib/utils';
import { messageErreur } from '../lib/erreurs';
import { libelle } from '../lib/libelles';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { PageHeader } from '../components/ui/page-header';
import { SectionCard } from '../components/dashboard';
import { LoadingState } from '../components/ui/loading-state';
import { EmptyState } from '../components/ui/empty-state';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';

const LOCATION_TYPES = [
  'capital',
  'city',
  'region_center',
  'border_post',
  'checkpoint',
  'fuel_depot',
  'station',
  'port',
  'customs_post',
  'transit_point',
  'delivery_site',
  'risk_zone',
  'custom',
].map((code) => [code, libelle('locationType', code)]);

/**
 * Douze types de zone, ce n'est pas douze couleurs.
 *
 * L'échelle catégorielle du produit ne compte que trois pas, et c'est délibéré:
 * au-delà, deux teintes voisines deviennent indiscernables (les neuf couleurs
 * d'étape mesurées à ΔE 5,3 dans `status.js` l'ont montré). Les types sont donc
 * regroupés en familles d'exploitation — ce qu'on stocke, ce qu'on livre, ce
 * qu'on franchit — et la couleur marque la famille, jamais le pays ni la ville.
 *
 * « Zone à risque » a sa propre entrée parce que c'est la seule famille que la
 * carte distingue elle aussi (cf. `ZoneLayer`): le rouge n'est pas décoratif,
 * il correspond à un cercle rouge à l'écran.
 */
const FAMILLES_ZONE = {
  depot: {
    label: 'Dépôt',
    couleur: 'hsl(var(--cat-hold))',
    types: ['fuel_depot', 'station', 'port'],
  },
  livraison: {
    label: 'Site de livraison',
    couleur: 'hsl(var(--cat-done))',
    types: ['delivery_site'],
  },
  frontiere: {
    label: 'Poste frontière',
    couleur: 'hsl(var(--cat-route))',
    types: ['border_post', 'customs_post', 'checkpoint'],
  },
  risque: {
    label: 'Zone à risque',
    couleur: 'hsl(var(--destructive))',
    types: ['risk_zone'],
  },
  autre: {
    label: 'Autre',
    couleur: 'hsl(var(--muted-foreground))',
    types: [],
  },
};

const FAMILLE_PAR_TYPE = Object.entries(FAMILLES_ZONE).reduce(
  (table, [cle, famille]) => {
    famille.types.forEach((type) => {
      table[type] = cle;
    });
    return table;
  },
  {},
);

const familleDe = (zone) =>
  FAMILLES_ZONE[FAMILLE_PAR_TYPE[zone?.type] || 'autre'];

/**
 * Ce que la carte distingue, maintenant qu'elle le peut.
 *
 * `ZoneLayer` est partagé par cinq écrans et ne connaissait qu'un axe:
 * l'avancement sur un corridor — zone franchie, zone à venir. Un dépôt et un
 * poste frontière y sortaient donc du même bleu, et cette légende promettait
 * quatre couleurs que le cercle ne portait pas.
 *
 * Le composant accepte désormais `colorBy="type"`, qui peint la famille
 * d'exploitation. L'avancement reste encodé par le trait pointillé, de sorte
 * que les deux lectures cohabitent sans se disputer la teinte — et que la
 * carte reste lisible sans dépendre de la couleur seule.
 */
const LEGENDE_CARTE = {
  depot: { label: 'Dépôt', color: 'hsl(var(--cat-hold))' },
  delivery: { label: 'Site de livraison', color: 'hsl(var(--cat-done))' },
  border: { label: 'Poste frontière', color: 'hsl(var(--cat-route))' },
  risk: { label: 'Zone à risque', color: 'hsl(var(--destructive))' },
  other: { label: 'Autre', color: 'hsl(var(--muted-foreground))' },
};

/**
 * Un drapeau se lit plus vite qu'un nom de pays dans une colonne de vingt
 * lignes, mais il ne remplace jamais le nom: il l'accompagne. Le pays est saisi
 * en texte libre, donc la table accepte le nom comme le code ISO, et retombe
 * sur une icône neutre quand elle ne reconnaît rien — un drapeau faux serait
 * pire que pas de drapeau.
 */
const DRAPEAUX = {
  ml: '🇲🇱',
  mali: '🇲🇱',
  bf: '🇧🇫',
  'burkina faso': '🇧🇫',
  burkina: '🇧🇫',
  ci: '🇨🇮',
  'cote d ivoire': '🇨🇮',
  sn: '🇸🇳',
  senegal: '🇸🇳',
  ne: '🇳🇪',
  niger: '🇳🇪',
  gh: '🇬🇭',
  ghana: '🇬🇭',
  tg: '🇹🇬',
  togo: '🇹🇬',
  bj: '🇧🇯',
  benin: '🇧🇯',
  gn: '🇬🇳',
  guinee: '🇬🇳',
  mr: '🇲🇷',
  mauritanie: '🇲🇷',
  ng: '🇳🇬',
  nigeria: '🇳🇬',
  td: '🇹🇩',
  tchad: '🇹🇩',
};

const normaliserPays = (pays) =>
  String(pays || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z]+/g, ' ')
    .trim()
    .toLowerCase();

const drapeauDe = (pays) => DRAPEAUX[normaliserPays(pays)] || null;

/** π r², en km². Le rayon seul ne dit pas ce qu'une zone couvre vraiment: 500 m
 *  et 1 500 m sont dans un rapport de 1 à 9 en surface, pas de 1 à 3. */
const surfaceKm2 = (rayonMetres) => {
  const rayon = Number(rayonMetres || 0);
  if (!Number.isFinite(rayon) || rayon <= 0) return 0;
  return (Math.PI * rayon * rayon) / 1e6;
};

const emptyForm = {
  name: '',
  code: '',
  type: 'custom',
  country: '',
  region: '',
  city: '',
  latitude: '',
  longitude: '',
  radius_meters: 500,
  description: '',
  status: 'active',
  notify_on_enter: true,
  notify_on_exit: true,
  notify_on_dwell: false,
  dwell_minutes: '',
  whatsapp_numbers_json: [],
  client_id: null,
  station_id: null,
};

/**
 * Les trois lieux qu'une société pose elle-même (lot 051). Chacun fixe le type
 * et un rayon d'usage; le reste se remplit sur la carte. Dépôt et site client
 * servent aux étapes proposées (chargement, arrivée au dépôt, livraison), y
 * compris pour une distribution sans corridor.
 */
const MODELES_ZONE = {
  depot: { type: 'fuel_depot', radius_meters: 400 },
  client: { type: 'delivery_site', radius_meters: 300 },
  station: { type: 'station', radius_meters: 200 },
};

const SANS_RATTACHEMENT = '__aucun__';

/**
 * Une zone partagée — poste frontière, ville jalon — n'appartient à personne et
 * sert à tout le monde. Elle reste visible depuis n'importe quelle société,
 * sinon aucun corridor ne se dessine; mais la déplacer casserait la détection
 * de tous les autres clients. Le serveur refuse (403); l'écran doit le dire
 * avant le clic, pas après.
 */
const isShared = (location) =>
  location?.is_shared ?? location?.organization_id == null;

export default function GeoLocationsPage() {
  const { isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  // Familles de zones décochées dans la fenêtre « Zones » de la carte.
  const [famillesMasquees, setFamillesMasquees] = useState([]);
  const [events, setEvents] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  // Clients et stations proposés pour rattacher une zone: lus une fois, à
  // l'ouverture du formulaire, toutes pages comprises.
  const [clients, setClients] = useState(null);
  const [stations, setStations] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        status: statusFilter === 'all' ? undefined : statusFilter,
        type: typeFilter === 'all' ? undefined : typeFilter,
        q: query || undefined,
        limit: 1000,
      };
      const [locResp, eventResp, prefs, truckResp] = await Promise.all([
        geoLocationsAPI.getAll(params),
        geoLocationsAPI.getEvents({ limit: 20 }),
        usersAPI.getMyPreferences().catch(() => ({})),
        /* Un événement geofence ne stocke que des identifiants. « Camion
           a3f2… » n'apprend rien à un exploitant: il connaît ses matricules.
           L'appel est secondaire, son échec ne doit pas vider la page. */
        trucksAPI.getAll().catch(() => []),
      ]);
      setLocations(locResp.data || locResp || []);
      setEvents(eventResp.data || eventResp || []);
      setWhatsappNumber(prefs?.geo_alerts?.whatsapp_number || '');
      setTrucks(truckResp?.data || truckResp || []);
    } catch (e) {
      console.error(e);
      toast.error(messageErreur(e, 'Impossible de charger les zones géographiques'));
    } finally {
      setLoading(false);
    }
  }, [query, statusFilter, typeFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = (modele) => {
    setSelected(null);
    setFormData({
      ...emptyForm,
      ...(typeof modele === 'string' ? MODELES_ZONE[modele] : null),
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    if (!dialogOpen) return;
    const lire = (chargeur, ecrire) =>
      Promise.resolve()
        .then(() => fetchAllPages((params) => chargeur(params)))
        .then((resultat) => ecrire(resultat?.items || []))
        .catch(() => ecrire([]));
    if (formData.type === 'delivery_site' && clients === null) lire(clientsAPI.getAllPaged, setClients);
    if (formData.type === 'station' && stations === null) lire(stationsAPI.getAllPaged, setStations);
  }, [dialogOpen, formData.type, clients, stations]);

  const optionsRattachement = useCallback(
    (lignes, repli) => [
      { value: SANS_RATTACHEMENT, label: repli },
      ...(lignes || []).map((ligne) => ({
        value: ligne.id,
        label: ligne.name || ligne.nom || ligne.id,
        description: ligne.city || undefined,
      })),
    ],
    [],
  );

  const canWrite = useCallback(
    (location) => isSuperAdmin || !isShared(location),
    [isSuperAdmin],
  );

  const openEdit = (location) => {
    setSelected(location);
    setFormData({
      ...emptyForm,
      ...location,
      latitude: String(location.latitude ?? ''),
      longitude: String(location.longitude ?? ''),
      radius_meters: Number(location.radius_meters || 500),
      dwell_minutes: location.dwell_minutes ? String(location.dwell_minutes) : '',
      whatsapp_numbers_json: location.whatsapp_numbers_json || [],
    });
    setDialogOpen(true);
  };

  const payload = useMemo(() => ({
    ...formData,
    latitude: Number(formData.latitude),
    longitude: Number(formData.longitude),
    radius_meters: Number(formData.radius_meters || 500),
    dwell_minutes: formData.dwell_minutes ? Number(formData.dwell_minutes) : null,
    whatsapp_numbers_json: formData.whatsapp_numbers_json || [],
    // Le rattachement n'a de sens que pour le type qui le porte.
    client_id: formData.type === 'delivery_site' ? formData.client_id || null : null,
    station_id: formData.type === 'station' ? formData.station_id || null : null,
  }), [formData]);

  const save = async () => {
    if (selected && !canWrite(selected)) {
      toast.error('Cette zone est partagée par la plateforme : elle ne peut pas être modifiée ici');
      return;
    }
    if (!payload.name || !payload.country || !Number.isFinite(payload.latitude) || !Number.isFinite(payload.longitude)) {
      toast.error('Nom, pays, latitude et longitude sont requis');
      return;
    }
    try {
      if (selected) {
        await geoLocationsAPI.update(selected.id, payload);
        toast.success('Localisation mise à jour');
      } else {
        await geoLocationsAPI.create(payload);
        toast.success('Localisation créée');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      console.error(e);
      toast.error(messageErreur(e, 'La zone n’a pas pu être enregistrée'));
    }
  };

  const toggleStatus = async (location) => {
    if (location.status === 'active') await geoLocationsAPI.deactivate(location.id);
    else await geoLocationsAPI.activate(location.id);
    load();
  };

  const remove = async (location) => {
    await geoLocationsAPI.delete(location.id);
    toast.success('Localisation supprimée');
    load();
  };

  const saveWhatsapp = async () => {
    await usersAPI.updateMyPreferences({
      geo_alerts: {
        whatsapp_enabled: Boolean(whatsappNumber),
        whatsapp_number: whatsappNumber,
      },
    });
    toast.success('Numéro WhatsApp enregistré');
  };

  const activeLocations = useMemo(
    () => locations.filter((loc) => loc.status === 'active'),
    [locations],
  );
  const activeCenters = useMemo(
    () =>
      activeLocations
        .filter((loc) => loc.latitude != null && loc.longitude != null)
        .map((loc) => [loc.latitude, loc.longitude]),
    [activeLocations],
  );

  /* La légende se déduit de ce qui est réellement tracé, et non d'une liste
     écrite à la main: une légende qui annonce une couleur absente de la carte
     est une légende fausse. */
  const legendItems = useMemo(() => {
    const famillesPresentes = new Set(
      filtrerZones(activeLocations, famillesMasquees).map(zoneFamily),
    );
    return Object.entries(LEGENDE_CARTE)
      .filter(([cle]) => famillesPresentes.has(cle))
      .map(([key, item]) => ({ key, ...item }));
  }, [activeLocations, famillesMasquees]);

  /* Le pays est filtré côté écran et non côté serveur, pour que la liste des
     pays proposés reste celle du référentiel chargé: filtrée en amont, elle
     s'effondrerait à un seul choix dès la première sélection. */
  const countryOptions = useMemo(() => {
    const pays = new Set(
      locations.map((loc) => loc.country).filter(Boolean),
    );
    return Array.from(pays).sort((a, b) => a.localeCompare(b, 'fr'));
  }, [locations]);
  const countryFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'Tous les pays' },
      ...countryOptions.map((pays) => ({ value: pays, label: pays })),
    ],
    [countryOptions],
  );

  const visibleLocations = useMemo(
    () =>
      countryFilter === 'all'
        ? locations
        : locations.filter((loc) => loc.country === countryFilter),
    [locations, countryFilter],
  );
  const pagination = usePagination(visibleLocations, { reinitialiser: countryFilter });

  const zonesById = useMemo(
    () => new Map(locations.map((loc) => [loc.id, loc])),
    [locations],
  );
  const trucksById = useMemo(
    () => new Map((trucks || []).map((truck) => [truck.id, truck])),
    [trucks],
  );

  const libelleCamion = useCallback(
    (truckId) => {
      const truck = trucksById.get(truckId);
      const matricule = truck?.matricule || truck?.plate_number;
      if (matricule) return matricule;
      return 'non identifié';
    },
    [trucksById],
  );

  // `statusFilter` vaut 'active' par défaut: il ne compte comme filtre que
  // s'il a été changé, sinon toute liste vide passerait pour « filtrée ».
  const hasReferentialFilters =
    Boolean(query) ||
    typeFilter !== 'all' ||
    countryFilter !== 'all' ||
    statusFilter !== 'active';

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      {/* La recherche transverse a quitté cet écran: elle est désormais portée
          par la barre supérieure de l'application, visible partout. En garder
          une copie ici affichait deux champs de recherche côte à côte, dont
          l'un ne faisait que rediriger vers `/search`. Le champ du référentiel,
          plus bas, reste: lui filtre vraiment la liste des zones. */}
      <PageHeader
        eyebrow={isSuperAdmin ? 'Super admin' : 'Administration'}
        title="Zones géographiques"
        description={
          isSuperAdmin
            ? 'Référentiel géographique opérationnel et alertes geofence.'
            : 'Vos sites de livraison et de dépôt, plus les postes frontières partagés par la plateforme.'
        }
        actions={
          <>
            <Button variant="outline" onClick={load}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              Rafraîchir
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="ajouter-zone">
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  Ajouter
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => openCreate('depot')}>Un dépôt</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => openCreate('client')}>Le site d’un client</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => openCreate('station')}>Une station</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => openCreate('autre')}>Une autre zone</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <Card>
        <CardContent className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4 p-4 md:p-5">
          <div className="flex min-w-72 flex-1 items-start gap-3">
            {/* Pastille verte: WhatsApp est un canal, pas un emplacement — la
                couleur marque le canal, et `lucide-react` n'embarque pas de
                logo de marque, donc le glyphe de conversation le porte. */}
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-success/10 text-success">
              <MessageCircle className="h-5 w-5" aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-1">
                <h2 className="text-base font-bold leading-tight text-foreground">
                  Notifications WhatsApp admin
                </h2>
                <Aide titre="Notifications WhatsApp admin">
                  Recevez une alerte lors de l’entrée ou de la sortie d’un camion
                  dans une zone.
                </Aide>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-56">
              <Label htmlFor="whatsapp-admin">Numéro WhatsApp</Label>
              <Input
                id="whatsapp-admin"
                value={whatsappNumber}
                onChange={(e) => setWhatsappNumber(e.target.value)}
                placeholder="+223…"
              />
            </div>
            <Button onClick={saveWhatsapp}>Enregistrer</Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.85fr)_minmax(0,1fr)]">
        <SectionCard icon={MapPin} title="Carte des zones actives">
          {dialogOpen ? (
            <div className="flex h-[520px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/50 text-sm text-muted-foreground">
              Carte des zones masquée pendant l’édition
            </div>
          ) : (
            <div className="relative isolate z-0 h-[520px] overflow-hidden rounded-lg border border-border">
              {/* Le référentiel tient rarement dans le cadre par défaut:
                  on cadre sur les zones réellement enregistrées. */}
              <MapSurface
                layer="streets"
                fitTo={activeCenters}
                legend={<MapLegend items={legendItems} />}
                overlay={
                  /* Le compteur est posé sur la carte plutôt que dans
                     l'en-tête de la section: `SectionCard` réserve sa droite à
                     un lien de sortie, et un décompte se lit mieux contre ce
                     qu'il dénombre. */
                  <div className="absolute right-3 top-3 z-[400] flex items-start gap-2">
                    <Badge variant="info" className="pointer-events-none shadow-sm">
                      {activeLocations.length} zones actives
                    </Badge>
                    <ZonesFiltre
                      zones={activeLocations}
                      masquees={famillesMasquees}
                      onChange={setFamillesMasquees}
                      couleurs
                    />
                  </div>
                }
              >
                {/* Au-delà d'une douzaine de zones, les étiquettes permanentes
                    cessent de nommer la carte et commencent à la cacher: sur le
                    corridor malien elles s'empilent autour de Bamako au point
                    qu'on ne lit plus ni les noms ni le fond. La couleur porte
                    alors la famille, et le nom se lit en survolant le cercle ou
                    dans le référentiel en dessous. */}
                <ZoneLayer
                  zones={activeLocations}
                  onSelect={openEdit}
                  colorBy="type"
                  famillesMasquees={famillesMasquees}
                  showLabels={activeLocations.length <= 12}
                />
              </MapSurface>
            </div>
          )}
        </SectionCard>

        <SectionCard
          icon={Radio}
          title="Derniers événements"
          actionLabel="Voir tous"
          onAction={() => navigate('/tracking')}
          contentClassName="space-y-3"
        >
          {events.map((event) => {
            const entree = event.event_type === 'entered';
            const zone = zonesById.get(event.geo_location_id);
            return (
              <div key={event.id} className="flex items-start gap-3">
                {/* Entrée et sortie sont deux états opposés du même camion:
                    la teinte les sépare, la flèche les dit aussi — jamais la
                    couleur seule. */}
                <span
                  className={
                    entree
                      ? 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-success/10 text-success'
                      : 'flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive'
                  }
                >
                  {entree ? (
                    <ArrowDownLeft className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-foreground">
                    {entree ? 'Entrée dans une zone' : 'Sortie de zone'}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    Camion {libelleCamion(event.truck_id)} ·{' '}
                    {zone?.name || 'Zone inconnue'}
                  </p>
                </div>
                <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground/80">
                  {formatRelativeTime(event.created_at)}
                </span>
              </div>
            );
          })}

          {/* Rien ne se crée depuis cette colonne: les événements sont écrits
              par le passage des camions. La fin de liste doit donc expliquer
              d'où viendra la suite, pas proposer un bouton. */}
          <div className="flex flex-col items-center gap-1 rounded-lg border border-dashed border-border/70 px-4 py-6 text-center">
            <Radio className="h-5 w-5 text-muted-foreground/60" aria-hidden="true" />
            <div className="flex items-center gap-1">
              <p className="text-sm font-medium text-muted-foreground">
                {events.length > 0
                  ? 'Aucun autre événement'
                  : 'Aucun événement pour l’instant'}
              </p>
              <Aide titre="Événements de zone">
                {activeLocations.length === 0
                  ? 'Il faut d’abord une zone active pour qu’une entrée ou une sortie de camion soit détectée.'
                  : 'Les derniers mouvements s’afficheront ici en temps réel.'}
              </Aide>
            </div>
          </div>
        </SectionCard>
      </div>

      <SectionCard icon={Globe2} title="Référentiel des zones">
        <div className="mb-4 grid gap-2 md:grid-cols-[minmax(0,1fr)_11rem_11rem_9rem_auto]">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && load()}
              className="pl-9"
              type="search"
              aria-label="Rechercher une zone"
              placeholder="Rechercher une zone…"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger aria-label="Filtrer par type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              {LOCATION_TYPES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
          <SearchableSelect
            aria-label="Filtrer par pays"
            placeholder="Tous les pays"
            searchPlaceholder="Rechercher un pays…"
            emptyMessage="Aucun pays trouvé"
            value={countryFilter}
            onValueChange={setCountryFilter}
            options={countryFilterOptions}
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger aria-label="Filtrer par statut"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">En service</SelectItem>
              <SelectItem value="inactive">Hors service</SelectItem>
              <SelectItem value="all">Toutes</SelectItem>
            </SelectContent>
          </Select>
          {/* Recherche et type partent au serveur: l'entonnoir relance la
              requête sans obliger à revenir dans le champ pour taper Entrée. */}
          <Button
            variant="outline"
            size="icon"
            onClick={load}
            aria-label="Appliquer les filtres"
            title="Appliquer les filtres"
          >
            <Filter className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>

        {loading ? (
          <LoadingState shape="table" />
        ) : visibleLocations.length === 0 ? (
          <EmptyState
            variant={hasReferentialFilters ? 'filtered' : 'empty'}
            /* Écran entièrement neuf: l'illustration de marque explique mieux
               qu'un glyphe ce que la page contiendra. Liste filtrée à vide:
               l'icône suffit, l'écran est déjà occupé autour. */
            illustration={hasReferentialFilters ? undefined : 'zone'}
            icon={MapPin}
            title={
              hasReferentialFilters
                ? 'Aucune zone ne correspond'
                : 'Aucune zone enregistrée'
            }
            description={
              hasReferentialFilters
                ? 'Vos zones existent toujours : la recherche ou les filtres de type, de pays et de statut en masquent l’affichage.'
                : 'Une zone est un point de la carte avec un rayon — un dépôt, un poste frontière, une zone à risque. Dès qu’un camion y entre ou en sort, l’événement est daté et une alerte WhatsApp peut partir.'
            }
            actionLabel={hasReferentialFilters ? undefined : 'Ajouter une zone'}
            onAction={openCreate}
          />
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom de la zone</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Pays</TableHead>
                  <TableHead>Rayon / surface</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Partage</TableHead>
                  <TableHead>Dernière modif.</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.lignes.map((loc) => {
                  const famille = familleDe(loc);
                  const partagee = isShared(loc);
                  const drapeau = drapeauDe(loc.country);
                  const active = loc.status === 'active';
                  return (
                    <TableRow key={loc.id}>
                      <TableCell>
                        <button
                          type="button"
                          className="flex items-start gap-2.5 text-left"
                          onClick={() => openEdit(loc)}
                        >
                          {/* La pastille reprend la couleur de la famille, et
                              la famille est écrite juste à côté en colonne
                              TYPE: la teinte accélère le balayage, le texte
                              porte l'information. */}
                          <span
                            className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: famille.couleur }}
                            aria-hidden="true"
                          />
                          <span className="min-w-0">
                            <span className="block font-semibold text-foreground">
                              {loc.name}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              {loc.code ? `${loc.code} · ` : ''}
                              {loc.city || loc.region || 'Sans localité précisée'}
                            </span>
                          </span>
                        </button>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {libelle('locationType', loc.type, famille.label)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="flex items-center gap-2 whitespace-nowrap text-sm text-foreground">
                          {drapeau ? (
                            <span aria-hidden="true">{drapeau}</span>
                          ) : (
                            <Globe2
                              className="h-4 w-4 text-muted-foreground"
                              aria-hidden="true"
                            />
                          )}
                          {loc.country || '—'}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        <span className="block text-sm text-foreground">
                          {Number(loc.radius_meters || 0).toLocaleString('fr-FR')} m
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {surfaceKm2(loc.radius_meters).toLocaleString('fr-FR', {
                            maximumFractionDigits: 2,
                          })}{' '}
                          km²
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={active ? 'success' : 'destructive'}>
                          {active ? 'En service' : 'Hors service'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {partagee ? (
                          <Badge variant="info" className="gap-1 font-normal">
                            <Users className="h-3 w-3" aria-hidden="true" />
                            Partagée
                          </Badge>
                        ) : (
                          <Badge variant="muted" className="gap-1 font-normal">
                            <Lock className="h-3 w-3" aria-hidden="true" />
                            Privée
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                        {formatRelativeTime(loc.updated_at || loc.created_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              aria-label={`Actions de la zone ${loc.name || ''}`.trim()}
                            >
                              <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-60">
                            {canWrite(loc) ? (
                              <>
                                <DropdownMenuItem onSelect={() => openEdit(loc)}>
                                  <Pencil className="h-4 w-4" aria-hidden="true" />
                                  Modifier la zone
                                </DropdownMenuItem>
                                <DropdownMenuItem onSelect={() => toggleStatus(loc)}>
                                  {active ? (
                                    <PowerOff className="h-4 w-4" aria-hidden="true" />
                                  ) : (
                                    <Power className="h-4 w-4" aria-hidden="true" />
                                  )}
                                  {active ? 'Désactiver' : 'Activer'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onSelect={() => remove(loc)}
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                                  Supprimer
                                </DropdownMenuItem>
                              </>
                            ) : (
                              /* Ni entrée grisée ni menu vide: la zone est
                                 commune à toutes les sociétés, et le dire une
                                 fois vaut mieux qu'un 403 après le clic. On
                                 ne propose donc pas de la modifier. */
                              <>
                                <DropdownMenuLabel className="font-normal text-xs text-muted-foreground">
                                  Référentiel de la plateforme, non modifiable
                                </DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onSelect={() => openEdit(loc)}>
                                  <Eye className="h-4 w-4" aria-hidden="true" />
                                  Consulter la zone
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
        {!loading && visibleLocations.length > 0 ? (
          <BarrePagination pagination={pagination} nom="zones" />
        ) : null}
      </SectionCard>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="z-[1200] max-h-[92vh] max-w-4xl overflow-y-auto overflow-x-hidden">
          <DialogHeader>
            <div className="flex items-center gap-1">
              <DialogTitle>
                {!selected
                  ? 'Nouvelle localisation'
                  : canWrite(selected)
                    ? 'Modifier la localisation'
                    : 'Localisation partagée'}
              </DialogTitle>
              {selected && !canWrite(selected) && (
                <Aide titre="Localisation partagée">
                  Cette zone est un repère commun à toutes les sociétés — un poste
                  frontière, une ville jalon. La déplacer changerait la détection de
                  tous les autres clients : elle se consulte ici, elle se modifie
                  par la plateforme.
                </Aide>
              )}
            </div>
          </DialogHeader>
          <div className="grid min-w-0 gap-4 md:grid-cols-2">
            <div><Label>Nom *</Label><Input value={formData.name} onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))} /></div>
            <div><Label>Code</Label><Input value={formData.code || ''} onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))} /></div>
            <div>
              <Label>Type</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData((p) => ({ ...p, type: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{LOCATION_TYPES.map(([value, label]) => <SelectItem key={value} value={value}>{label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {formData.type === 'delivery_site' && (
              <div>
                <Label>Client</Label>
                <SearchableSelect
                  value={formData.client_id || SANS_RATTACHEMENT}
                  onValueChange={(value) =>
                    setFormData((p) => ({ ...p, client_id: value === SANS_RATTACHEMENT ? null : value }))
                  }
                  options={optionsRattachement(clients, 'Tous les camions de la société')}
                  placeholder="Choisir un client"
                  searchPlaceholder="Rechercher un client…"
                  emptyMessage="Aucun client enregistré."
                />
              </div>
            )}
            {formData.type === 'station' && (
              <div>
                <Label>Station</Label>
                <SearchableSelect
                  value={formData.station_id || SANS_RATTACHEMENT}
                  onValueChange={(value) =>
                    setFormData((p) => ({ ...p, station_id: value === SANS_RATTACHEMENT ? null : value }))
                  }
                  options={optionsRattachement(stations, 'Tous les camions de la société')}
                  placeholder="Choisir une station"
                  searchPlaceholder="Rechercher une station…"
                  emptyMessage="Aucune station enregistrée."
                />
              </div>
            )}
            <div><Label>Pays *</Label><Input value={formData.country} onChange={(e) => setFormData((p) => ({ ...p, country: e.target.value }))} /></div>
            <div><Label>Région</Label><Input value={formData.region || ''} onChange={(e) => setFormData((p) => ({ ...p, region: e.target.value }))} /></div>
            <div><Label>Ville</Label><Input value={formData.city || ''} onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))} /></div>
            <div><Label>Latitude *</Label><Input type="number" value={formData.latitude} onChange={(e) => setFormData((p) => ({ ...p, latitude: e.target.value }))} /></div>
            <div><Label>Longitude *</Label><Input type="number" value={formData.longitude} onChange={(e) => setFormData((p) => ({ ...p, longitude: e.target.value }))} /></div>
            <div><Label>Rayon (en mètres)</Label><Input type="number" value={formData.radius_meters} onChange={(e) => setFormData((p) => ({ ...p, radius_meters: e.target.value }))} /></div>
            <div>
              <Label>Statut</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData((p) => ({ ...p, status: value }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">En service</SelectItem><SelectItem value="inactive">Hors service</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2"><Label>Description</Label><Input value={formData.description || ''} onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))} /></div>
            <div className="min-w-0 md:col-span-2">
              <Label>Sélection sur carte</Label>
              {/* Poser un poste frontière se joue à la dizaine de mètres. Le
                  picker recentre sur le point, ouvre au niveau de la rue et
                  laisse déplacer le repère et étirer le rayon à la main. */}
              <ZonePicker
                latitude={formData.latitude}
                longitude={formData.longitude}
                radiusMeters={formData.radius_meters}
                name={formData.name}
                onChange={(patch) => setFormData((p) => ({ ...p, ...patch }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {selected && !canWrite(selected) ? 'Fermer' : 'Annuler'}
            </Button>
            {(!selected || canWrite(selected)) && (
              <Button onClick={save}>Enregistrer</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
